import React from "react";
import { Link } from "react-router-dom";
import { Check, BookOpenText, Users, Timer, Quote, ArrowRight } from "lucide-react";
import Panda from "../components/Panda";
import { CurveBottom, WaveTop, Cloud, Sun, Bamboo, ArcText, Coin } from "../components/Decor";
import { modules, testimonials, BOOK_URL } from "../data/mock";
import { useProgress } from "../context/ProgressContext";

const ModuleCard = ({ m, progress }) => (
  <div className="flex flex-col items-center group" data-testid={`module-card-${m.id}`}>
    <Link
      to={`/lessons/${m.slug}`}
      className="relative w-full max-w-[420px] h-[210px] rounded-2xl overflow-hidden shadow-md hover:shadow-2xl hover:-translate-y-1.5 transition-[transform,box-shadow] duration-300 block"
      style={{ background: m.color }}
    >
      <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 80% 20%, #fff 0, transparent 55%)` }} />
      <div className="absolute left-6 top-5">
        <p className="text-xs font-bold uppercase tracking-widest" style={{ color: m.dark }}>
          Ages {m.ages}
        </p>
        <h3 className="font-logo text-3xl leading-none mt-1 text-white drop-shadow-[0_2px_0_rgba(0,0,0,0.35)]">{m.short}</h3>
        <p className="text-sm font-semibold mt-2 max-w-[165px] leading-snug" style={{ color: m.dark }}>
          {m.title}
        </p>
      </div>
      <div className="absolute -right-2 -bottom-6 group-hover:-translate-y-2 transition-transform duration-300">
        <Panda size={140} accessory={m.pandaAccessory} scarf={m.pandaScarf} item={m.pandaItem} />
      </div>
      <span className="absolute left-6 bottom-5 btn-pill text-white shadow-lg" style={{ background: m.button }}>
        {progress > 0 ? `Continue (${progress}%)` : "Start lesson"}
      </span>
      {progress === 100 && (
        <span className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white text-[#2e8b57] flex items-center justify-center shadow">
          <Check size={18} />
        </span>
      )}
    </Link>
    <p className="mt-4 text-center text-[#1b8bc9] font-semibold text-lg max-w-[320px] leading-snug">{m.tagline}</p>
  </div>
);

const Home = () => {
  const { moduleProgress, panda } = useProgress();

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative bg-[#7d5fb2] overflow-hidden" data-testid="hero">
        <Bamboo className="absolute -left-6 -top-6 hidden md:block opacity-95" height={460} />
        <Bamboo className="absolute -right-8 top-10 hidden lg:block opacity-90" height={380} flip />
        <Cloud className="absolute left-[8%] top-[42%] hidden md:block cloud-drift" face size={230} />
        <Cloud className="absolute right-[6%] top-[22%] hidden md:block cloud-drift-slow" size={170} />
        <Coin className="absolute left-[22%] top-[18%] hidden lg:block coin-float" size={54} />
        <Coin className="absolute right-[24%] bottom-[28%] hidden lg:block coin-float-slow" size={44} />

        <div className="relative max-w-5xl mx-auto px-6 pt-8 pb-28 text-center">
          <div className="inline-block bg-white rounded-[40px] px-8 py-3 shadow-lg">
            <p className="font-serif italic text-[#1b8bc9] font-bold text-xl leading-none" style={{ fontFamily: "Literata, serif" }}>Teach your child</p>
            <p className="font-logo text-5xl leading-none -mt-1">
              {["C", "r", "e", "d", "i", "t"].map((l, i) => (
                <span key={i} style={{ color: ["#e5356f", "#f7931e", "#63c79a", "#1b8bc9", "#7d5fb2", "#f3c932"][i], display: "inline-block", transform: `rotate(${i % 2 ? 5 : -5}deg)` }}>{l}</span>
              ))}
            </p>
          </div>

          <h1 className="mt-8 text-white font-bold text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-3xl mx-auto" style={{ fontFamily: "Literata, serif" }}>
            Inspire lifelong financial confidence with our playful, age-based credit lessons for kids
          </h1>

          <div className="mt-10 relative inline-block">
            <div className="bg-white rounded-3xl p-3 shadow-2xl rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
              <div className="rounded-2xl bg-[#6dcdf2] w-[300px] sm:w-[440px] h-[210px] sm:h-[290px] relative overflow-hidden">
                <div className="absolute bottom-0 left-0 right-0 h-16 bg-[#63c79a] rounded-t-[50%] scale-x-125" />
                <Sun className="absolute -top-8 -right-8" size={120} />
                <Cloud className="absolute left-4 top-6" size={110} />
                <div className="absolute left-1/2 -translate-x-1/2 bottom-2">
                  <Panda size={panda ? 170 : 160} accessory={panda?.accessory || "none"} scarf={panda?.scarf || "#e5356f"} item="coin" animate />
                </div>
                <div className="absolute left-6 bottom-6 bg-white/95 rounded-2xl px-4 py-2 shadow text-left">
                  <p className="text-[10px] uppercase tracking-widest font-bold text-[#7d5fb2]">Promise jar</p>
                  <p className="font-bold text-[#0c4a6e]">Save $2 of $4</p>
                  <div className="h-2 w-24 bg-[#e9f9fd] rounded-full mt-1 overflow-hidden"><div className="h-full w-1/2 bg-[#63c79a]" /></div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <Link to={panda?.name ? "/lessons" : "/panda"} className="btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white shadow-xl" data-testid="hero-play-button">
              Play now for free
            </Link>
          </div>
        </div>
        <CurveBottom color="#ffffff" />
      </section>

      {/* MODULE CARDS */}
      <section className="py-16 sm:py-24 bg-white" data-testid="modules-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-16 justify-items-center">
            {modules.map((m) => (
              <ModuleCard key={m.id} m={m} progress={moduleProgress(m.id)} />
            ))}
            <div className="flex flex-col items-center justify-center text-center w-full max-w-[420px] h-[210px] rounded-2xl border-4 border-dashed border-[#c9ecf5] p-6">
              <p className="font-bold text-[#1b8bc9] text-xl" style={{ fontFamily: "Literata, serif" }}>Not sure where to start?</p>
              <p className="text-gray-600 mt-2 text-sm">Start at your child's current age - or earlier. Every lesson builds on the one before it.</p>
              <Link to="/lessons" className="mt-4 inline-flex items-center gap-2 font-bold text-[#e5356f] hover:gap-3 transition-all">
                Pick by age <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ADVENTURES SECTION */}
      <section className="relative bg-[#6dcdf2]" data-testid="adventures-section">
        <div className="bg-white"><WaveTop color="#6dcdf2" /></div>
        <Sun className="absolute right-[4%] top-8 hidden md:block" size={220} />
        <Cloud className="absolute left-[3%] top-[45%] hidden md:block cloud-drift" size={280} />
        <Cloud className="absolute right-[10%] bottom-[8%] hidden lg:block cloud-drift-slow" size={200} />
        <div className="relative max-w-5xl mx-auto px-6 pt-6 pb-24">
          <ArcText text="Adventures in learning!" />
          <div className="grid md:grid-cols-2 gap-10 items-center -mt-6">
            <div className="md:pl-12">
              <h2 className="text-3xl font-bold text-[#0d3d2b] mb-6" style={{ fontFamily: "Literata, serif" }}>Our lessons are...</h2>
              <ul className="space-y-4 text-lg text-[#0d3d2b]">
                {[
                  [BookOpenText, "Built from 30+ years of mortgage banking and housing counseling experience."],
                  [Users, "Made for families - every lesson has a parent script you can read aloud."],
                  [Timer, "Short, mindful screen time: learn, talk, practice, then go do it for real."],
                ].map(([Icon, text], i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-0.5 w-8 h-8 rounded-full bg-white/80 text-[#1b8bc9] flex items-center justify-center flex-shrink-0"><Icon size={18} /></span>
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-x-4 bottom-2 h-6 bg-[#0d3d2b]/10 rounded-full blur-md" />
                <Panda size={230} accessory="glasses" scarf="#f7931e" item="bamboo" animate />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE BOOK */}
      <section className="bg-white py-20" data-testid="book-section">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-[280px_1fr] gap-12 items-center">
          <div className="flex justify-center">
            <a href={BOOK_URL} target="_blank" rel="noreferrer" className="book-cover group" aria-label="Get the book on Amazon">
              <div className="book-spine" />
              <div className="book-front">
                <p className="text-[10px] tracking-[0.3em] uppercase text-white/80">An age-based family curriculum</p>
                <h3 className="text-white font-bold text-2xl leading-tight mt-2" style={{ fontFamily: "Literata, serif" }}>Teach Your Child Credit</h3>
                <div className="my-3 flex justify-center"><Panda size={64} accessory="cap" scarf="#f3c932" item="coin" /></div>
                <p className="text-white/90 text-xs">Building Lifelong Financial Confidence</p>
                <p className="text-white font-bold text-sm mt-3">Ken Bossard, MBA</p>
              </div>
            </a>
          </div>
          <div>
            <p className="text-[#e5356f] font-bold uppercase tracking-widest text-sm">From the book</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0c4a6e] mt-2" style={{ fontFamily: "Literata, serif" }}>Credit is a tool, not a trap.</h2>
            <p className="mt-4 text-gray-700 text-lg leading-relaxed">
              Families who talk openly about credit raise more confident, capable adults. This site brings the five age-based modules from Ken Bossard's book to life - each with learning objectives, a parent conversation script, a worksheet, and a real family case study.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href={BOOK_URL} target="_blank" rel="noreferrer" className="btn-pill bg-[#f7931e] hover:bg-[#dd7f0f] text-white shadow-lg" data-testid="get-book-button">Get the book on Amazon</a>
              <Link to="/parents/fundamentals" className="btn-pill bg-white border-2 border-[#1b8bc9] text-[#1b8bc9] hover:bg-[#e9f9fd]">Parents' area</Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#fff8e1] py-20" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-center text-3xl sm:text-4xl font-bold text-[#8a6d00] mb-12" style={{ fontFamily: "Literata, serif" }}>What families are saying</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white rounded-3xl p-8 shadow-md hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300 relative">
                <Quote className="text-[#f3c932] absolute -top-4 left-6" size={40} fill="#f3c932" />
                <p className="text-gray-700 leading-relaxed mt-2">{t.text}</p>
                <div className="mt-6 flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-[#e9f9fd] flex items-center justify-center overflow-hidden">
                    <Panda size={34} accessory={["none", "glasses", "bowtie"][i]} scarf={["#e5356f", "#1b8bc9", "#63c79a"][i]} />
                  </div>
                  <div>
                    <p className="font-bold text-[#0c4a6e]">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
